<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairCarouselIndicator } from '@/ui/primitives/FairCarouselIndicator'
import { FairIcon } from '@/ui/primitives/FairIcon'
import FairHomeHeroBannerHowTo from './FairHomeHeroBannerHowTo.vue'
import {
  FAIR_HOME_HERO_BANNER_SLIDES,
  clampFairHomeHeroBannerPage,
  type FairHomeHeroBannerSlideId,
} from './fair-home-hero.data'

const FairHomeHeroBannerWays = defineAsyncComponent(
  () => import('./FairHomeHeroBannerWays.vue'),
)
const FairHomeHeroBannerVideo = defineAsyncComponent(
  () => import('./FairHomeHeroBannerVideo.vue'),
)

const { t } = useI18n()

interface FairHomeHeroBannerProps {
  page?: number
}

const props = withDefaults(defineProps<FairHomeHeroBannerProps>(), {
  page: 1,
})

const emit = defineEmits<{
  'update:page': [page: number]
  next: []
  prev: []
}>()

const TRANSITION_MS = 450
const SWIPE_THRESHOLD_PX = 48
const WHEEL_THRESHOLD = 36

const total = FAIR_HOME_HERO_BANNER_SLIDES.length

const rootRef = ref<HTMLElement | null>(null)
const slideDirection = ref<'next' | 'prev'>('next')
const isAnimating = ref(false)

const pointerStartX = ref(0)
const pointerStartY = ref(0)
const pointerTracking = ref(false)
const pointerId = ref<number | null>(null)

let wheelAccum = 0
let wheelLocked = false
let animTimer: ReturnType<typeof setTimeout> | null = null

const safePage = computed(() =>
  clampFairHomeHeroBannerPage(props.page, total),
)

const activeSlideId = computed<FairHomeHeroBannerSlideId>(
  () => FAIR_HOME_HERO_BANNER_SLIDES[safePage.value - 1]?.id ?? 'how-to',
)

const transitionName = computed(
  () => `fair-home-hero-banner-slide-${slideDirection.value}`,
)

function directionFor(from: number, to: number): 'next' | 'prev' {
  if (from === total && to === 1) return 'next'
  if (from === 1 && to === total) return 'prev'
  return to > from ? 'next' : 'prev'
}

function lockAnimation() {
  isAnimating.value = true
  if (animTimer) clearTimeout(animTimer)
  animTimer = setTimeout(() => {
    isAnimating.value = false
    animTimer = null
  }, TRANSITION_MS)
}

function setPage(page: number): boolean {
  const next = clampFairHomeHeroBannerPage(page, total)
  if (next === safePage.value || isAnimating.value) return false

  slideDirection.value = directionFor(safePage.value, next)
  lockAnimation()
  emit('update:page', next)
  return true
}

function handleNext() {
  if (setPage(safePage.value + 1)) emit('next')
}

function handlePrev() {
  if (setPage(safePage.value - 1)) emit('prev')
}

function onWheel(event: WheelEvent) {
  // Trackpad horizontal swipe → deltaX; keep vertical page scroll free
  if (Math.abs(event.deltaX) < 8 || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
    return
  }

  event.preventDefault()

  if (wheelLocked || isAnimating.value) return

  wheelAccum += event.deltaX

  if (wheelAccum > WHEEL_THRESHOLD) {
    wheelAccum = 0
    wheelLocked = true
    handleNext()
    window.setTimeout(() => {
      wheelLocked = false
    }, TRANSITION_MS)
  }
  else if (wheelAccum < -WHEEL_THRESHOLD) {
    wheelAccum = 0
    wheelLocked = true
    handlePrev()
    window.setTimeout(() => {
      wheelLocked = false
    }, TRANSITION_MS)
  }
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return Boolean(
    target.closest(
      'a, button, input, textarea, select, [role="button"], .fair-carousel-indicator',
    ),
  )
}

function onPointerDown(event: PointerEvent) {
  if (event.button !== 0 || isAnimating.value) return
  if (isInteractiveTarget(event.target)) return

  pointerTracking.value = true
  pointerId.value = event.pointerId
  pointerStartX.value = event.clientX
  pointerStartY.value = event.clientY
  rootRef.value?.setPointerCapture(event.pointerId)
}

function finishPointer(event: PointerEvent) {
  if (!pointerTracking.value || event.pointerId !== pointerId.value) return

  pointerTracking.value = false
  pointerId.value = null

  const dx = event.clientX - pointerStartX.value
  const dy = event.clientY - pointerStartY.value

  if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) {
    return
  }

  if (dx < 0) handleNext()
  else handlePrev()
}

function onPointerUp(event: PointerEvent) {
  finishPointer(event)
}

function onPointerCancel(event: PointerEvent) {
  if (event.pointerId !== pointerId.value) return
  pointerTracking.value = false
  pointerId.value = null
}

onMounted(() => {
  rootRef.value?.addEventListener('wheel', onWheel, { passive: false })
})

onBeforeUnmount(() => {
  rootRef.value?.removeEventListener('wheel', onWheel)
  if (animTimer) clearTimeout(animTimer)
})
</script>

<template>
  <div
    ref="rootRef"
    class="fair-home-hero-banner"
    role="region"
    aria-roledescription="carousel"
    :aria-label="t('fair_home.hero.carousel.region')"
    @pointerdown="onPointerDown"
    @pointerup="onPointerUp"
    @pointercancel="onPointerCancel"
  >
    <div class="fair-home-hero-banner__viewport">
      <Transition :name="transitionName">
        <FairHomeHeroBannerHowTo
          v-if="activeSlideId === 'how-to'"
          :key="'how-to'"
        />
        <FairHomeHeroBannerWays
          v-else-if="activeSlideId === 'ways'"
          :key="'ways'"
        />
        <FairHomeHeroBannerVideo
          v-else
          :key="'video'"
          :active="activeSlideId === 'video'"
        />
      </Transition>
    </div>

    <button
      type="button"
      class="fair-home-hero-banner__nav fair-home-hero-banner__nav--prev"
      :aria-label="t('fair_home.hero.carousel.prev')"
      @click="handlePrev"
    >
      <FairIcon
        name="arrow-left"
        :size="24"
        color="var(--fair-color-white-80)"
      />
    </button>

    <button
      type="button"
      class="fair-home-hero-banner__nav fair-home-hero-banner__nav--next"
      :aria-label="t('fair_home.hero.carousel.next')"
      @click="handleNext"
    >
      <FairIcon
        name="arrow-right"
        :size="24"
        color="var(--fair-color-white-80)"
      />
    </button>

    <div class="fair-home-hero-banner__dots">
      <FairCarouselIndicator
        variant="stripes"
        tone="on-media"
        :page="safePage"
        :total="total"
        @update:page="setPage"
      />
    </div>
  </div>
</template>

<style scoped>
.fair-home-hero-banner {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border: var(--fair-home-hero-banner-border, 2px) solid
    var(--fair-color-white-100, #fff);
  border-radius: var(--fair-home-hero-banner-radius, 60px);
  box-sizing: border-box;
  container-type: size;
  touch-action: pan-y;
  cursor: grab;
  user-select: none;
}

.fair-home-hero-banner:active {
  cursor: grabbing;
}

.fair-home-hero-banner__viewport {
  position: absolute;
  inset: 0;
}

.fair-home-hero-banner__nav {
  position: absolute;
  top: 50%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(40 / 835 * 100cqi);
  height: calc(40 / 835 * 100cqi);
  min-width: 22px;
  min-height: 22px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  translate: 0 -50%;
  cursor: pointer;
  color: var(--fair-color-white-100, #fff);
}

.fair-home-hero-banner__nav :deep(.fair-icon) {
  width: 100%;
  height: 100%;
}

.fair-home-hero-banner__nav--prev {
  left: 2%;
}

.fair-home-hero-banner__nav--next {
  right: 2%;
}

.fair-home-hero-banner__dots {
  position: absolute;
  bottom: 0;
  left: 50%;
  z-index: 2;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: calc(20 / 835 * 100cqi);
  translate: -50% 0;
  box-sizing: border-box;
  cursor: default;
}

/* Slide transitions — next = incoming from right */
.fair-home-hero-banner-slide-next-enter-active,
.fair-home-hero-banner-slide-next-leave-active,
.fair-home-hero-banner-slide-prev-enter-active,
.fair-home-hero-banner-slide-prev-leave-active {
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

.fair-home-hero-banner-slide-next-leave-active,
.fair-home-hero-banner-slide-prev-leave-active {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.fair-home-hero-banner-slide-next-enter-from {
  transform: translateX(100%);
}

.fair-home-hero-banner-slide-next-leave-to {
  transform: translateX(-100%);
}

.fair-home-hero-banner-slide-prev-enter-from {
  transform: translateX(-100%);
}

.fair-home-hero-banner-slide-prev-leave-to {
  transform: translateX(100%);
}
</style>
